import { biDisplayFormat } from '@euler/model/bi/format';
import {
  BiCell,
  BiFieldRole,
  BiFieldType,
  BiResultColumn,
} from '@euler/model/bi/result';
import {
  BiColor,
  BiDeltaDirection,
  BiFormat,
  BiKpiCompare,
  BiKpiGroupItem,
  BiVizType,
} from '@euler/model/bi/viz';
import {
  BiGalleryCategory,
  BiGalleryCategoryKey,
  BiGalleryDashboardCard,
} from '../functions/biGalleryTypes';
import {
  cents,
  kGalleryFields as f,
  galleryReady,
  galleryResult,
} from './biGalleryFields';

const kThisMonth = { from: '2026-10-01', to: '2026-10-17' };
const kLastMonth = { from: '2026-09-01', to: '2026-09-17' };

function kpiCard(
  title: string,
  column: BiResultColumn,
  current: BiCell,
  options: {
    previous?: BiCell;
    deltaGoodWhen?: BiDeltaDirection;
    showDelta?: boolean;
  } = {},
): BiGalleryDashboardCard {
  const compared = options.previous !== undefined;
  return {
    surface: 'dashboard',
    title,
    viz: {
      type: BiVizType.Kpi,
      value: column.key,
      ...(compared ? { compare: BiKpiCompare.PreviousPeriod } : {}),
      ...(compared &&
      (options.deltaGoodWhen !== undefined || options.showDelta === false)
        ? {
            style: {
              kpi: {
                showDelta: options.showDelta ?? true,
                deltaGoodWhen: options.deltaGoodWhen ?? BiDeltaDirection.Up,
              },
            },
          }
        : {}),
    },
    data: galleryReady(galleryResult([column], [[current]], kThisMonth), {
      previous: compared
        ? galleryResult([column], [[options.previous ?? null]], kLastMonth)
        : undefined,
    }),
  };
}

const plain = kpiCard('本月工单数', f.orders, 652);

function partMeasure(
  key: string,
  zh: string,
  format: BiFormat,
  side: number,
): BiResultColumn {
  return {
    key,
    label: { zh, en: key },
    type: BiFieldType.Number,
    role: BiFieldRole.Measure,
    format: biDisplayFormat(format),
    side,
  };
}

/** A KPI group over totals of orders and inspection findings. */
const kGroupColumns = [
  partMeasure('q1__order_count', '接车台次', BiFormat.Integer, 0),
  partMeasure('q2__task_count', '检测台次', BiFormat.Integer, 1),
  partMeasure('q2__abnormal_count', '检出异常数', BiFormat.Integer, 1),
  partMeasure('q2__abnormal_per_task', '单车异常数', BiFormat.Decimal, 1),
  partMeasure('q1__first_visit_count', '首次进店', BiFormat.Integer, 0),
  partMeasure('q2__converted_count', '报修成交', BiFormat.Integer, 1),
];

function groupCard(
  title: string,
  items: BiKpiGroupItem[],
  current: BiCell[],
  previous?: BiCell[],
): BiGalleryDashboardCard {
  const columns = kGroupColumns.slice(0, items.length);
  return {
    surface: 'dashboard',
    title,
    viz: {
      type: BiVizType.KpiGroup,
      items,
      ...(previous ? { compare: BiKpiCompare.PreviousPeriod } : {}),
    },
    data: galleryReady(galleryResult(columns, [current], kThisMonth), {
      previous: previous
        ? galleryResult(columns, [previous], kLastMonth)
        : undefined,
    }),
  };
}

const kGroupItems: BiKpiGroupItem[] = [
  { value: 'q1__order_count', color: BiColor.Blue },
  { value: 'q2__task_count', color: BiColor.Green },
  { value: 'q2__abnormal_count', name: '检出异常数', color: BiColor.Amber },
  { value: 'q2__abnormal_per_task', name: '单车异常数', color: BiColor.Red },
];

export const kKpiCategory: BiGalleryCategory = {
  key: BiGalleryCategoryKey.Kpi,
  name: '指标卡',
  description: '单个数字、环比、上升 / 下降为好、金额 / 百分比 / 时长',
  preview: kpiCard('本月营收', f.revenue, cents(272300), {
    previous: cents(248700),
  }),
  variants: [
    { key: 'plain', name: '普通', note: '无对比', card: plain },
    {
      key: 'group',
      name: '指标组',
      note: 'kpi_group：两份数据合计，四个数并排',
      card: groupCard('车辆检测概览', kGroupItems, [233, 193, 520, 2.6943]),
    },
    {
      key: 'group-compared',
      name: '指标组 · 对比上期',
      note: 'compare = previous_period，每个数各自涨跌',
      card: groupCard(
        '车辆检测概览',
        kGroupItems,
        [233, 193, 520, 2.6943],
        [251, 180, 498, 2.7667],
      ),
    },
    {
      key: 'group-six',
      name: '指标组 · 六个数',
      note: '放不下时均匀换行；未设颜色的取配色',
      card: groupCard(
        '本周经营',
        [
          ...kGroupItems,
          { value: 'q1__first_visit_count' },
          { value: 'q2__converted_count' },
        ],
        [233, 193, 520, 2.6943, 40, 5],
      ),
    },
    {
      key: 'up-good',
      name: '环比上升 · 上升为好',
      note: 'compare = previous_period',
      card: kpiCard('本月营收', f.revenue, cents(272300), {
        previous: cents(248700),
      }),
    },
    {
      key: 'down-bad',
      name: '环比下降 · 上升为好',
      note: '下降标红',
      card: kpiCard('本月复购率', f.returnRate, 0.438, { previous: 0.483 }),
    },
    {
      key: 'down-good',
      name: '环比下降 · 下降为好',
      note: 'kpi.deltaGoodWhen = down，下降标绿',
      card: kpiCard('平均施工时长', f.jobTime, 46, {
        previous: 58,
        deltaGoodWhen: BiDeltaDirection.Down,
      }),
    },
    {
      key: 'up-bad',
      name: '环比上升 · 下降为好',
      note: 'kpi.deltaGoodWhen = down，上升标红',
      card: kpiCard('平均施工时长', f.jobTime, 135, {
        previous: 104,
        deltaGoodWhen: BiDeltaDirection.Down,
      }),
    },
    {
      key: 'flat',
      name: '持平',
      card: kpiCard('本月新客户', f.newCustomers, 132, { previous: 132 }),
    },
    {
      key: 'no-delta',
      name: '隐藏涨跌',
      note: 'kpi.showDelta = false，仍显示上期',
      card: kpiCard('本月营收', f.revenue, cents(272300), {
        previous: cents(248700),
        showDelta: false,
      }),
    },
    {
      key: 'currency',
      name: '金额（8 位）',
      note: 'format = currency',
      card: kpiCard('年度累计营收', f.revenue, cents(23456789.5)),
    },
    {
      key: 'percent',
      name: '百分比',
      note: 'format = percent',
      card: kpiCard('本月复购率', f.returnRate, 0.4832),
    },
    {
      key: 'duration',
      name: '时长',
      note: 'format = duration（分钟）',
      card: kpiCard('平均施工时长', f.jobTime, 135),
    },
    {
      key: 'decimal',
      name: '小数',
      note: 'format = decimal',
      card: kpiCard('平均评分', f.rating, 4.8667),
    },
    {
      key: 'small',
      name: '矮卡片',
      note: '2 行高：去掉标签和底框',
      card: {
        ...kpiCard('本月营收', f.revenue, cents(272300), {
          previous: cents(248700),
        }),
        rows: 2,
      },
    },
  ],
};
